export default {
  en: {
    title: 'Review sharing',
    // Shared between the org settings page and the per-supplier modal - both
    // tab over the same community list. See ReviewSharingCommunityTabs.vue.
    communityTabs: {
      disabledTooltip: 'Review sharing is not turned on for this community',
    },
    // The assessment drawer's tab listing peers' signals on one question.
    assessmentSignals: {
      // Under the peer's name on each entry. The viewer's own note on the same
      // question sits on the details tab, so the name is what makes this one theirs.
      sharedThrough: 'Shared through {communities}',
      viewReview: "View {org}'s review",
      // A control note records when it was last changed, not when it was raised.
      lastModified: 'Last modified {date}',
      markedCompliant: 'Marked compliant',
      markedNonCompliant: 'Marked non-compliant',
      // The row counted signals the tab then found none of: a peer stopped sharing
      // between the counts loading and the tab opening.
      emptyTitle: 'No signals to show',
      empty:
        'Peers have stopped sharing what they recorded here. Reload the page to update the counts.',
    },
    communitySignals: {
      title: '{community} community signals',
      // For a row naming a community the viewer's own list does not carry.
      titleWithoutCommunity: 'Community signals',
      // No empty message: the endpoint omits a community with no eligible peer, so
      // the box cannot be empty.
      peerConnections: 'Fully connected peers',
      reviews: 'Reviews',
      // Claims more than the number can: counts are gated on sharing, so a zero can
      // also mean peers recorded something and withhold it.
      noReviews: 'No one has reviewed this supplier',
      signals: 'Open actions',
      noSignals: 'No open actions on this supplier',
      // Outcomes rather than the connection review dropdown's Approve/Reject/None,
      // which name the action of choosing one.
      recommendation: {
        none: 'No decision',
        approve: 'Approved',
        reject: 'Rejected',
      },
    },
    signalsDrawer: {
      // The panel's own words, so a drawer reached by link reads as the section it
      // opens from.
      title: '{community} community signals',
      // For a community the viewer's own list does not carry.
      titleWithoutCommunity: 'Signals',
      empty: 'No peer reviews in this community yet.',
      // Filters run after the visibility gate, so a peer withholding a value is absent
      // from a filtered list rather than shown without it: what did not match can also
      // be a review the viewer cannot see.
      emptyFiltered: 'No reviews match your filters',
      clearFilters: 'Clear filters',
      tooManyPeersTitle: 'Too many organisations to filter',
      tooManyPeers:
        'This community is too large to filter on what peers share. Narrow it down by organisation, or clear the filters to see the full list.',
    },
    requestAccessModal: {
      title: 'Request access to review',
      // A request is for one peer's review of one supplier, so it names both.
      description: "You are requesting access to {peer}'s review of {supplier}.",
      confirm: 'Request',
      error: 'Could not send the request. Try again.',
    },
    // The filter bar's own labels: "Reviews" and "Tags" are not what the rows they
    // narrow are called on a card.
    filters: {
      organisations: 'Organisations',
      openActions: 'Open actions',
      pipelineStatus: 'Pipeline status',
      tags: 'Tags',
      reviews: 'Reviews',
    },
    card: {
      pipelineStatus: 'Pipeline status',
      classifications: 'Classifications',
      // The review drawer's own wording, since the row is the same one there.
      products: 'Products used',
      labels: 'Labels',
      recommendation: 'Recommendation',
      reviewNotes: 'Review notes',
      risks: 'Risks',
      remediations: 'Remediations',
      overrides: 'Overrides',
      view: 'View',
      requestAccess: 'Request full access',
      // Sits where View and Request full access do, on a peer sharing no details.
      noAccess: 'No access',
      // Nothing to withhold, which is what separates this from No access above.
      nothingRecorded: 'No review available',
      // The same pill, where the review would only repeat what the card shows.
      nothingToView: 'No review available',
      accessRequested: 'Access requested',
      // Hover text on the disabled pills above, keyed by the pill's label.
      disabledReasons: {
        noAccess: "This organisation doesn't share its review details in this community",
        nothingRecorded: 'This organisation has not added any review actions on this supplier',
        nothingToView: 'No other information is available',
        accessRequested: "This organisation hasn't responded to your request yet",
      },
    },

    // One peer's full review, opened from a card. The row labels repeat the card's
    // deliberately, so the same row does not read as two different things.
    reviewDrawer: {
      // The accessible name when the drawer is reached by link and the card
      // carrying the peer's name has not been paged in.
      title: 'Shared review',
      classifications: 'Classifications',
      products: 'Products used',
      labels: 'Labels',
      reviewDate: 'Review date',
      recommendation: 'Recommendation',
      reviewNotes: 'Review notes',
      // Under a section holding more entries than it opens with. `count` is the
      // number still hidden, not the section's total.
      showMore: 'Show {count} more',
      showLess: 'Show less',
      // Heads the five entry sections below as one group, which is what separates
      // them from the review notes above.
      openNotesAndActions: 'Open notes & actions',
      risks: 'Open risks',
      remediations: 'Open remediations',
      // The peer's private notes on its connection to this supplier, not the
      // supplier's own notes.
      privateNotes: 'Supplier notes',
      controlNotes: 'Control notes',
      overrides: 'Overrides',
      // Which control an entry hangs off. The product only appears where the same
      // control carries an entry per product, which is what separates them. `Raised
      // on` reads as raised against that control, not as a date.
      control: 'Raised on {reference}',
      controlForProduct: 'Raised on {reference} for {product}',
      // No reference to be raised on, so this one only places the entry.
      product: 'For {product}',
      // An override names its control in its own title rather than in a box beneath
      // it. A reference this build cannot name falls back to the box, which still
      // carries the question.
      overridden: 'Overridden compliance on {reference}',
      overriddenForProduct: 'Overridden compliance on {reference} for {product}',
      raisedOn: 'Raised on {date}',
      dueBy: 'Due by {date}',
      compliant: 'Compliant',
      // Reachable only when a grant is withdrawn between the list loading and the
      // drawer opening, so it says what changed rather than reading as a failure.
      // One message: not-a-community-member arrives as the same bare 403.
      notSharedTitle: 'Review no longer shared',
      notShared: 'This organisation has stopped sharing this review with you.',
      // Every row can go with what it would have held, so a peer that shares
      // categories and records nothing under them has an empty drawer. Reached from a
      // card that offered View, so it says what the peer holds rather than reading as
      // a failure to load.
      nothingRecordedTitle: 'Nothing recorded',
      nothingRecorded:
        'This organisation has not recorded anything under the categories it shares with you.',
      // Distinguishes a withheld category from one shared but empty.
      withheldCategories: 'Not shared with you: {categories}',
    },

    // Shared by the supplierOverrides and clientAccess sections below.
    categoriesCount: '{count} category | {count} categories',

    categoryEditor: {
      // Each consumer renders these; the editor owns only the checkboxes.
      heading: 'What can they view',
      description: 'These items will be available to view once you give them access to do so',
      allDetailsLabel: 'All details',
      // Suffixes a locked category, so a screen reader reads the state with the name.
      alreadySharedLabel: '{category} · Already shared',
      categoriesError: 'Pick at least one category',
      lastCategoryTooltip: 'At least one detail must be selected',
      categories: {
        review_summary: 'Review notes',
        private_notes: 'Private notes on supplier',
        control_notes: 'Private notes on controls',
        risks: 'Risks',
        remediations: 'Remediations',
        overrides: 'Overrides',
        labels: 'Labels',
      },
    },

    accessTypeSelect: {
      label: 'Access type',
      modes: {
        full_access: 'Shared with community',
        access_on_request: 'Access on request',
        no_details: 'No access',
      },
    },

    // Shared by ReviewSharingDivergenceBanner.vue, used on both the org
    // settings page and the per-supplier modal - both mean the same thing
    // (this level has its own pinned setting), so they share one message. The
    // modal swaps in divergenceDefault below once it knows the org default.
    divergenceBanner: {
      message: "You've overridden your organisation's sharing defaults",
      revertButton: 'Revert',
    },

    settings: {
      description:
        'See and manage how much review detail your organisation shares with each community by default.',
      modeHints: {
        full_access:
          'All organisations in this community can see the review details you give access to automatically',
        access_on_request:
          'Organisations in your community can request access to your review details. You can control what each organisation can see.',
        no_details:
          'All details of your reviews will be hidden from organisations in this community',
      },
      // Shown only when switching to no_details while at least one
      // organisation already has granted access - full_access and
      // access_on_request never strand an existing grant the way no_details
      // does.
      modeChangeConfirmTitle: 'Switch to "No access"?',
      modeChangeConfirmDescription:
        "All details of your reviews will be hidden from organisations in this community and any organisation you've given access to will lose it.",
      modeChangeConfirmAction: 'Switch to "No access"',
      revertError: 'Failed to revert to the community default',
      saveError: 'Failed to save your sharing settings',
      notMemberError: "Your organisation isn't an active member of this community",
      fetchError: 'Failed to load review sharing settings',
    },

    supplierOverrides: {
      heading: 'Suppliers with overridden access',
      description:
        'Suppliers your organisation has given a different access type from the one set above.',
      columnMember: 'Supplier',
      columnAccessType: 'Overridden access type',
      columnActions: 'Actions',
      edit: 'Edit',
      fetchError: 'Failed to load suppliers with overrides',
    },

    clientAccess: {
      heading: 'Community members with granted access',
      description:
        'Community members your organisation has granted access to one or more of its reviews.',
      columnClient: 'Community member',
      columnSupplier: 'Supplier they have access to',
      columnAccess: 'What can they access',
      columnActions: 'Actions',
      edit: 'Edit',
      fetchError: 'Failed to load community members with granted access',
    },

    // The per-supplier sharing modal (FEDX-299), reached from the supplier
    // kebab. Reuses accessTypeSelect/categoryEditor/divergenceBanner above.
    supplierModal: {
      title: 'Supplier sharing settings',
      modeHints: {
        full_access:
          'All organisations in this community can see the review details you give access to automatically',
        access_on_request:
          'Organisations in your community can request access to this review. You can control what each organisation can see.',
        no_details:
          'All details of this review will be hidden from organisations in this community',
      },
      categoriesHeading: 'What can they view',
      categoriesDescription:
        'These items will be available to view once you give them access to do so',
      noDetailsDescription:
        'Any review actions you have taken on this supplier will be hidden from your community',
      divergenceDefault: "You've overridden your organisation's default of {orgDefault}",
      // Shown only when switching to no_details while at least one
      // organisation already has granted access - full_access and
      // access_on_request never strand an existing grant the way no_details
      // does.
      modeChangeConfirmTitle: 'Switch to "No access"?',
      modeChangeConfirmDescription:
        "All details of your review of this supplier will be hidden from organisations in this community and any organisation you've given access to will lose it.",
      modeChangeConfirmAction: 'Switch to "No access"',
      saveError: "Failed to save this supplier's sharing settings",
      revertError: "Failed to revert to your organisation's sharing defaults",
      notMemberError: "Your organisation isn't an active member of this community",
      fetchError: "Failed to load this supplier's sharing settings",
      grantModal: {
        addTitle: 'Set access for {org}',
        addConfirm: 'Add',
        editTitle: 'Edit access for {org}',
        back: 'Back',
        removeAccess: 'Remove access',
        removeAccessConfirmTitle: 'Remove access?',
        removeAccessConfirmDescription:
          "This organisation will no longer be able to view any of this supplier's shared review details.",
        removeAccessError: 'Failed to remove access for this organisation',
        error: 'Failed to save access for this organisation',
      },
      grantRow: {
        saveError: "Failed to update this organisation's access",
        // The revoke half of the update already succeeded when this fires,
        // so this organisation's access is now fully removed, not unchanged.
        partialFailureError:
          "This organisation's access was removed, but the new selection couldn't be granted. Try again.",
      },
      accessOnRequest: {
        heading: 'Organisations with access',
        searchPlaceholder: 'Search organisations',
        add: 'Add',
        editAccess: 'Edit access',
        noResults: 'No organisations found',
        fetchError: 'Failed to load organisations with access',
        searchError: 'Failed to search organisations',
        noViewerSelected: 'Search for and select an organisation',
      },
      accessRequests: {
        heading: 'Access requests',
        description: 'Accept or reject access for these organisations in your community',
        review: 'Review request',
      },
    },
  },
};
